import { useState, useEffect } from 'react';
import { taskQueries, taskMutations } from '../../lib/database/index';
import { Task, Department } from '../../types';
import { useSupabase } from './useSupabase';
import { logger } from '../../utils/logger';

export function useTasks() {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isReady } = useSupabase();

  useEffect(() => {
    if (!isReady) return;

    const fetchTasks = async () => {
      try {
        setIsLoading(true);
        const data = await taskQueries.getTasks();
        
        if (!data) {
          throw new Error('No tasks found');
        }
        
        // Convert to application Task type
        const mappedTasks = data.map(task => ({
          id: task.id,
          title: task.title,
          description: task.description || '',
          assignedTo: task.assigned_to,
          assignedBy: task.assigned_by,
          department: task.department,
          priority: task.priority,
          dueDate: new Date(task.due_date),
          status: task.status,
          createdAt: new Date(task.created_at),
          updatedAt: new Date(task.updated_at),
          acknowledged: task.acknowledged,
          acknowledgedAt: task.acknowledged_at ? new Date(task.acknowledged_at) : undefined,
          comments: task.comments?.map(comment => ({
            id: comment.id,
            taskId: comment.task_id,
            userId: comment.user_id,
            content: comment.content,
            createdAt: new Date(comment.created_at),
            userName: comment.user?.name || 'Unknown',
            userAvatar: comment.user?.avatar || ''
          })) || [],
          subtasks: task.subtasks?.map(subtask => ({
            id: subtask.id,
            title: subtask.title,
            description: subtask.description || '',
            dueDate: subtask.due_date ? new Date(subtask.due_date) : undefined,
            status: subtask.status,
            acknowledged: subtask.acknowledged,
            acknowledgedAt: subtask.acknowledged_at ? new Date(subtask.acknowledged_at) : undefined,
            completedAt: subtask.completed_at ? new Date(subtask.completed_at) : undefined,
            hasDueDate: !!subtask.due_date
          })) || []
        }));
        
        setTasks(mappedTasks);
        setError(null);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to fetch tasks';
        logger.error('Error fetching tasks:', err);
        setError(message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTasks();
  }, [isReady]);

  const createTask = async (taskData: {
    title: string;
    description: string;
    assignedTo: string;
    assignedBy: string;
    department: Department;
    priority: Task['priority'];
    dueDate: Date;
  }) => {
    try {
      setIsLoading(true);
      const result = await taskMutations.createTask({
        title: taskData.title,
        description: taskData.description,
        assigned_to: taskData.assignedTo,
        assigned_by: taskData.assignedBy,
        department: taskData.department,
        priority: taskData.priority,
        due_date: taskData.dueDate
      });
      
      if (!result) {
        throw new Error('Failed to create task');
      }
      
      // Add new task to state
      const newTask: Task = {
        id: result.id,
        title: result.title,
        description: result.description || '',
        assignedTo: result.assigned_to,
        assignedBy: result.assigned_by,
        department: result.department,
        priority: result.priority,
        dueDate: new Date(result.due_date),
        status: result.status,
        createdAt: new Date(result.created_at),
        updatedAt: new Date(result.updated_at),
        acknowledged: result.acknowledged,
        comments: [],
        subtasks: []
      };
      
      setTasks(prev => [newTask, ...prev]);
      setError(null);
      
      return newTask;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create task';
      logger.error('Error creating task:', err);
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const updateTaskStatus = async (taskId: string, status: Task['status']) => {
    try {
      setIsLoading(true);
      await taskMutations.updateTaskStatus(taskId, status);
      
      // Update local state
      setTasks(prev => 
        prev.map(task => 
          task.id === taskId 
            ? { ...task, status, updatedAt: new Date() } 
            : task
        )
      );
      
      setError(null);
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update task status';
      logger.error('Error updating task status:', err);
      setError(message);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const acknowledgeTask = async (taskId: string) => {
    try {
      setIsLoading(true);
      await taskMutations.acknowledgeTask(taskId);
      
      // Update local state
      setTasks(prev => 
        prev.map(task => 
          task.id === taskId 
            ? { 
                ...task, 
                acknowledged: true, 
                acknowledgedAt: new Date(),
                updatedAt: new Date(),
                status: task.status === 'PENDING' ? 'IN_PROGRESS' : task.status
              } 
            : task
        )
      );
      
      setError(null);
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to acknowledge task';
      logger.error('Error acknowledging task:', err);
      setError(message);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const addTaskComment = async (taskId: string, userId: string, content: string) => {
    try {
      setIsLoading(true);
      const result = await taskMutations.addTaskComment(taskId, userId, content);
      
      if (!result) {
        throw new Error('Failed to add comment');
      }
      
      // Fetch user info for the comment
      const user = await userQueries.getUserById(userId);
      
      // Update local state
      setTasks(prev => 
        prev.map(task => {
          if (task.id === taskId) {
            return {
              ...task,
              comments: [
                ...task.comments,
                {
                  id: result.id,
                  taskId: result.task_id,
                  userId: result.user_id,
                  content: result.content,
                  createdAt: new Date(result.created_at),
                  userName: user?.name || 'Unknown',
                  userAvatar: user?.avatar || ''
                }
              ],
              updatedAt: new Date()
            };
          }
          return task;
        })
      );
      
      setError(null);
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to add comment';
      logger.error('Error adding comment:', err);
      setError(message);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    tasks,
    isLoading,
    error,
    createTask,
    updateTaskStatus,
    acknowledgeTask,
    addTaskComment,
    getDepartmentTasks: (department: Department) => tasks.filter(task => task.department === department),
    getAssignedTasks: (userId: string) => tasks.filter(task => task.assignedTo === userId),
    getTasksByAssignor: (userId: string) => tasks.filter(task => task.assignedBy === userId)
  };
}